import { useState, useEffect, useCallback } from 'react'
import { db } from '@services/db.js'
import { upsertTopicoProgresso } from '@services/crud.js'
import { getTopicoContent } from '@content/topicos-content.js'
import { useChecklistGeral } from '@hooks/useChecklistGeral.js'
import {
  computeChecklistPendentes,
  type ModuloChecklistPendente,
} from '@utils/checklistPendentes.js'

export interface ChecklistPendentesData {
  modulos: ModuloChecklistPendente[]
  totalItens: number
  totalPassos: number
  isLoading: boolean
}

const INITIAL: ChecklistPendentesData = {
  modulos: [],
  totalItens: 0,
  totalPassos: 0,
  isLoading: true,
}

export function useChecklistPendentes() {
  const {
    modulos: modulosProgresso,
    isLoading: progressoLoading,
    reload: reloadProgresso,
  } = useChecklistGeral()
  const [data, setData] = useState<ChecklistPendentesData>(INITIAL)

  const load = useCallback(async () => {
    const progressoAll = await db.topicos_progresso.toArray()
    const result = computeChecklistPendentes(progressoAll, modulosProgresso)
    setData({
      ...result,
      isLoading: false,
    })
  }, [modulosProgresso])

  useEffect(() => {
    if (progressoLoading) return
    load()
  }, [load, progressoLoading])

  const toggleItem = useCallback(
    async (topicoId: string, moduloId: string, itemId: string) => {
      const contentItems = getTopicoContent(topicoId)?.checklistAplicabilidade ?? []
      const current = await db.topicos_progresso.where('topicoId').equals(topicoId).first()
      const currentItems = current?.checklistItems ?? []
      const dbMap = new Map(currentItems.map((i) => [i.id, i.concluido]))

      const newItems = contentItems.map((ci) => ({
        id: ci.id,
        texto: ci.texto,
        concluido: ci.id === itemId ? !(dbMap.get(ci.id) ?? false) : (dbMap.get(ci.id) ?? false),
      }))

      const checked = newItems.filter((i) => i.concluido).length
      const total = newItems.length
      const pct = total > 0 ? Math.round((checked / total) * 100) : 0

      await upsertTopicoProgresso(topicoId, moduloId, {
        checklistItems: newItems,
        percentualPratico: pct,
      })
      await reloadProgresso()
      await load()
    },
    [load, reloadProgresso],
  )

  return {
    ...data,
    isLoading: progressoLoading || data.isLoading,
    reload: load,
    toggleItem,
  }
}
